import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, FileText, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { BECAS } from "../data/content";

export const Becas = () => {
  const [selected, setSelected] = useState(null);

  return (
    <section id="becas" data-testid="becas-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-5">
            {BECAS.overline}
          </p>
          <h2 className="font-serif-display text-3xl md:text-5xl font-semibold text-[#2D1B4E] tracking-tight leading-[1.1]">
            {BECAS.title}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {BECAS.items.map((b, i) => {
            const Icon = b.icon;
            const dark = i % 3 === 1;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`beca-card-${i}`}
                className={`${
                  dark
                    ? "bg-[#2D1B4E] text-white"
                    : "bg-white border border-[#E8E4EE] text-[#2D1B4E]"
                } rounded-sm p-7 flex flex-col hover:-translate-y-1 transition-transform duration-300`}
              >
                <Icon strokeWidth={1.5} className={`w-8 h-8 ${dark ? "text-[#C7B8E6]" : "text-[#5B3E96]"}`} />
                <h3 className="font-serif-display text-xl font-semibold mt-4">{b.title}</h3>
                <p className={`text-sm mt-2 leading-relaxed ${dark ? "text-white/75" : "text-[#5F5670]"}`}>
                  {b.desc}
                </p>
                {b.requirements && (
                  <button
                    type="button"
                    onClick={() => setSelected(b)}
                    data-testid={`beca-requisitos-btn-${i}`}
                    className={`mt-5 inline-flex items-center gap-2 text-sm font-medium self-start rounded-sm px-4 py-2 transition-colors ${
                      dark
                        ? "bg-white/10 text-white hover:bg-white/20"
                        : "bg-[#2D1B4E] text-white hover:bg-[#1A1523]"
                    }`}
                  >
                    <FileText className="w-4 h-4" /> Ver requisitos
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="beca-requisitos-modal">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#2D1B4E] leading-tight text-left">
              Requisitos · {selected?.title}
            </DialogTitle>
          </DialogHeader>
          <ul className="mt-2 space-y-4">
            {selected?.requirements?.map((r, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 strokeWidth={1.5} className="w-5 h-5 text-[#5B3E96] shrink-0 mt-0.5" />
                <span className="text-sm text-[#1A1523] leading-relaxed">{r}</span>
              </li>
            ))}
          </ul>

          {selected?.documents && (
            <div className="mt-6 pt-6 border-t border-[#E8E4EE]">
              <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-4">
                Documentos y formatos
              </p>
              <div className="space-y-3">
                {selected.documents.map((d, i) => (
                  <a
                    key={i}
                    href={d.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`beca-doc-${i}`}
                    className="flex items-center justify-between gap-3 bg-[#F4F2F7] hover:bg-[#EDE8F5] border border-[#E8E4EE] rounded-sm px-4 py-3 transition-colors group"
                  >
                    <span className="flex items-center gap-3 text-sm text-[#1A1523]">
                      <FileText className="w-4 h-4 text-[#5B3E96] shrink-0" />
                      {d.label}
                    </span>
                    <Download className="w-4 h-4 text-[#5B3E96] group-hover:translate-y-0.5 transition-transform shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
